import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { IFiltersResponse, IOptionValue, IPagingParams, ITableColumnsResponse, IUserFilters } from 'src/app/models/generic-datatable/generic-datatable.model';
import { IBaseContactabilidadResponse, IContacto, IContactoResponse, ICreateUpdateContactoResponse, IDeleteContactoResponse } from 'src/app/models/modulo-cobranza/base-contactabilidad.model';
import { IGeneralResponse } from 'src/app/models/modulo-cobranza/general.model';
import { environment } from 'src/environments/environment';
import { IUserInfoCognito } from './cognito.service';
import { SessionService } from './session.service';
import { ICausalMora, ICreateUpdateGestionResponse, IDeleteGestionResponse, IGestion, IGestionesResponse, IResultadoGestion, ITipoGestion } from 'src/app/models/modulo-cobranza/gestiones.model';
import { of } from 'rxjs';
import { IAsignacion, IAsignacionesResponse, ICreateAsignacionResponse, ILineasDeNegocioResponse, IResponsableDeudor, ISucursalesResponse } from 'src/app/models/modulo-cobranza/asignaciones.model';
import moment from 'moment';
import { IDetalleMontoVencido, IDocumento, IFacturacion, IMontoVencido, IUpdateCobranzaResponse } from 'src/app/models/modulo-cobranza/vision-cliente.model';
import { IReportsByTypeResponse, IReportStructureResponse, ITipoReporteResponse } from 'src/app/models/modulo-cobranza/reporteria.model';
import { IAlerta, IAlertaPayload, IConfiguracionAlerta } from 'src/app/models/modulo-cobranza/alertas.model';

@Injectable({
    providedIn: 'root'
})
export class ModuloCobranzaService {
    private endpoint: string = environment.backend.moduloCobranza.url;
    private readonly REQUEST_ORIGIN = 'WEB';

    constructor(
        private _sessionService: SessionService,
        private http: HttpClient
    ) { }

    private getHeaders(idOrganizacion: string, idUsuario: string, requestOrigin: string): HttpHeaders {
        const headers = new HttpHeaders()
            .set("IDOrganizacion", idOrganizacion)
            .set("IDUsuario", idUsuario)
            .set("requestOrigin", requestOrigin)

        return headers
    }

    buildHttpParams(paramsObj?: Record<string, string | number | null | undefined>): HttpParams {
        let params = new HttpParams();

        if (!paramsObj) return params;

        for (const [key, value] of Object.entries(paramsObj)) {
            if (value != null && value !== '') {
                params = params.set(key, value);
            }
        }

        return params;
    }

    getTableColumnsConfig(): Observable<ITableColumnsResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<ITableColumnsResponse>(`${this.endpoint}general/columns`, { headers });
    }


    getGeneralFiltersConfiguration(): Observable<IFiltersResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<IFiltersResponse>(`${this.endpoint}general/filters`, { headers });
    }


    getGeneralList(httpParams: IPagingParams, userFilters?: IUserFilters): Observable<IGeneralResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        let params = new HttpParams({ fromObject: { ...httpParams } });

        if (userFilters) {
            for (const [key, value] of Object.entries(userFilters)) {
                if (value instanceof Date) {
                    params = params.set(
                        key,
                        moment(value).format('YYYY-MM-DD')
                    );

                    continue;
                }
                
                params = params.set(key, value);
            }
        }

        return this.http.get<IGeneralResponse>(`${this.endpoint}general/list`, { headers, params }).pipe(
            map(resp => {
                return {
                    export: resp.export,
                    items: resp.items.map(item => {
                        return {
                            ...item,
                            rutCuenta: `${item.rutCuenta}-${item.dv}`,
                        }
                    }),
                    pageInfo: resp.pageInfo
                }
            })
        );
    }

    getFiltersBaseContactabilidad(): Observable<IFiltersResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<IFiltersResponse>(`${this.endpoint}contacto/filters`, { headers });
    }

    getTableColumnsBaseContactabilidadConfig(): Observable<ITableColumnsResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<ITableColumnsResponse>(`${this.endpoint}contacto/columns`, { headers });
    }


    getBaseContactabilidadList(httpParams : IPagingParams, userFilters?: IUserFilters): Observable<IBaseContactabilidadResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        let params = new HttpParams({ fromObject: { ...httpParams } });

        if (userFilters) {
            for (const [key, value] of Object.entries(userFilters)) {

                if (value instanceof Date) {
                    params = params.set(
                        key,
                        moment(value).format('YYYY-MM-DD')
                    );

                    continue;
                }

                params = params.set(key, value);
            }
        }

        return this.http.get<IBaseContactabilidadResponse>(`${this.endpoint}contacto`, { headers, params })
    }

    getContactosBaseContactabilidad(httpParams : IPagingParams): Observable<IContactoResponse> {

        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        const params = new HttpParams({ fromObject: { ...httpParams } });

        return this.http.get<IContactoResponse>(`${this.endpoint}contacto`, { headers, params })
    }

    createUpdateContacto(contacto: IContacto): Observable<ICreateUpdateContactoResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.post<ICreateUpdateContactoResponse>(`${this.endpoint}contacto`, contacto, { headers });
    }

    deleteContacto(idContacto: number): Observable<IDeleteContactoResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        const payload = { id: idContacto };

        return this.http.post<IDeleteContactoResponse>(`${this.endpoint}contacto/delete`, payload, { headers });
    }

    getFiltersGestiones(): Observable<IFiltersResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<IFiltersResponse>(`${this.endpoint}gestiones/filters`, { headers });
    }

    getColumnsGestiones(): Observable<ITableColumnsResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<ITableColumnsResponse>(`${this.endpoint}gestiones/columns`, { headers });
    }

    getGestionesList(httpParams : IPagingParams, userFilters?: IUserFilters): Observable<IGestionesResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        let params = new HttpParams({ fromObject: { ...httpParams } });

        if (userFilters) {
            for (const [key, value] of Object.entries(userFilters)) {

                if (value instanceof Date) {
                    params = params.set(
                        key,
                        moment(value).format('YYYY-MM-DD')
                    );

                    continue;
                }

                params = params.set(key, value);
            }
        }

        return this.http.get<IGestionesResponse>(`${this.endpoint}gestiones`, { headers, params })
    }

    getTiposGestion(): Observable<ITipoGestion[]> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<ITipoGestion[]>(`${this.endpoint}configuration/tipo-gestion`, { headers });
    }

    getResultadosGestion(): Observable<IResultadoGestion[]> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<IResultadoGestion[]>(`${this.endpoint}configuration/resultado-gestion`, { headers });
    }

    getCausalesMora(): Observable<ICausalMora[]> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<ICausalMora[]>(`${this.endpoint}configuration/causal-mora`, { headers });
    }

    addGestion(gestion: IGestion): Observable<ICreateUpdateGestionResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.post<ICreateUpdateGestionResponse>(`${this.endpoint}gestiones/add`, gestion, { headers });
    }

    editGestion(gestion: IGestion): Observable<ICreateUpdateGestionResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.post<ICreateUpdateGestionResponse>(`${this.endpoint}gestiones/edit`, gestion, { headers });
    }

    deleteGestion(id: number): Observable<IDeleteGestionResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.post<IDeleteGestionResponse>(`${this.endpoint}gestiones/delete`, { id }, { headers });
    }

    getFiltersAsignaciones(): Observable<IFiltersResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<IFiltersResponse>(`${this.endpoint}asignaciones/filters`, { headers });
    }

    getColumnsAsignaciones(): Observable<ITableColumnsResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<ITableColumnsResponse>(`${this.endpoint}asignaciones/columns`, { headers });
    }

    getAsignacionesList(httpParams : IPagingParams, userFilters?: IUserFilters): Observable<IAsignacionesResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        let params = new HttpParams({ fromObject: { ...httpParams } });

        if (userFilters) {
            for (const [key, value] of Object.entries(userFilters)) {

                if (value instanceof Date) {
                    params = params.set(
                        key,
                        moment(value).format('YYYY-MM-DD')
                    );

                    continue;
                }

                params = params.set(key, value);
            }
        }

        return this.http.get<IAsignacionesResponse>(`${this.endpoint}asignaciones/list`, { headers, params })
    }

    getSucursalesList(): Observable<ISucursalesResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<ISucursalesResponse>(`${this.endpoint}asignaciones/sucursales`, { headers })
    }

    getLineasDeNegocioList(): Observable<ILineasDeNegocioResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<ILineasDeNegocioResponse>(`${this.endpoint}asignaciones/lineas_de_negocio`, { headers })
    }

    getResponsablesList(): Observable<IResponsableDeudor[]> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<IResponsableDeudor[]>(`${this.endpoint}asignaciones/responsables`, { headers })
    }

    addAsignacion(asignacion: IAsignacion): Observable<ICreateAsignacionResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.post<ICreateAsignacionResponse>(`${this.endpoint}asignaciones/add`, asignacion, { headers });
    }

    getVisionCliente(identificador: string): Observable<any> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        const params = this.buildHttpParams({ identificador });

        return this.http.get<IResponsableDeudor[]>(`${this.endpoint}cliente`, { headers, params })
    }

    updateCobranzaData(identificador: string): Observable<IUpdateCobranzaResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        const payload = { identificador };

        return this.http.post<IUpdateCobranzaResponse>(`${this.endpoint}cliente/reporte`, payload, { headers })
    }

    getMontosVencidos(): Observable<IMontoVencido[]> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();
    
        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        const data = [];
        for (let index = 0; index < 5; index++) {
            data.push({
                rut: '915020000-3',
                razonSocial: 'Salinas y Fabres S.A.',
                porVencer: 90539834,
                vencido: 150000000,
                total: 150000000,
                intereses: 10000000,
                totalMasIntereses: 160000000
            });
        }

        return of(data);
    
    }

    getDetalleMontosVencidos(): Observable<IDetalleMontoVencido[]> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();
    
        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        const data = [
            {
                concepto: 'Autycam',
            },
            {
                concepto: 'Camiones',
            },
            {
                concepto: 'Corportativos & Gen',
            },
            {
                concepto: 'Repuestos',
            },
            {
                concepto: 'Camiones',
            },
            {
                concepto: 'Equipos Especiales',
            },
            {
                concepto: 'Salfa Rent',
            },
            {
                concepto: 'Servicio Agrícola',
            },
            {
                concepto: 'Servicio Liviano',
            },
            {
                concepto: 'Servicio Pesado',
            },
            {
                concepto: 'Total',
            }
        ];

        return of(data);
    }

   getDocumentos(): Observable<IDocumento[]> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();
    
        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        const data = [];
        for (let index = 0; index < 5; index++) {
            data.push({
                DOC: 1,
                SOC: 2000,
                clase: 'ZP',
                tipoDocumento: 'Contabil. Pagos',
                nDoc: 200071057,
                asignacion: 'A003432424',
                cebe: '20CHAR10',
                lineaNegocio: 'Salfa Rent',
                referencia: '25306258655',
                VIP: 'I',
                CMEIO: '-',
                fechaDoc: '30/05/2024',
                vencimiento: '29/06/2024',
                mora: 192,
                importe: 976587,
                interes: 85002,
                respDeudor: 'CH',
                nPedidoOC: '-',
                vendedor: 'Eduardo Lillo'
            });
        }

        return of(data);
    }

    getFacturacion(identificador: string): Observable<IFacturacion> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();
    
        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        const params = this.buildHttpParams({ identificador });

        return this.http.get<IFacturacion>(`${this.endpoint}cliente/facturacion`, { headers, params });
    }

    getReportTypesReporteria(): Observable<ITipoReporteResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<ITipoReporteResponse>(`${this.endpoint}reportes/tipos`, { headers });  
    }

    getReportsByTypeReporteria(tipoReporte: string): Observable<IReportsByTypeResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        const params = this.buildHttpParams({ tipo: tipoReporte });

        return this.http.get<IReportsByTypeResponse>(`${this.endpoint}reportes/listar`, { headers, params });
    }

    getReportStructureReporteria(key: string): Observable<IReportStructureResponse> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        const params = this.buildHttpParams({ key });

        return this.http.get<IReportStructureResponse>(`${this.endpoint}reportes/estructura`, { headers, params });
    }

    getReportDataBySourceReporteria(source = '', userFilters?: IUserFilters): any {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        let params = new HttpParams();

        if (userFilters) {
            for (const [key, value] of Object.entries(userFilters)) {
                if (value instanceof Date) {
                    params = params.set(
                        key,
                        moment(value).format('YYYY-MM-DD')
                    );

                    continue;
                }
                
                params = params.set(key, value);
            }
        }

        return this.http.get<any>(`${this.endpoint}${source}`, { headers, params });
    }

    getAutocompleteByPath(path: string, q: string): Observable<{options: IOptionValue[]}> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);
        const params = this.buildHttpParams({ q });

        return this.http.get<{options: IOptionValue[]}>(`${this.endpoint}${path}`, { headers, params });
    }

    getExcelByPath(path: string, userFilters?: IUserFilters): Observable<string> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        let params = new HttpParams();

        if (userFilters) {
            for (const [key, value] of Object.entries(userFilters)) {
                if (value instanceof Date) {
                    params = params.set(
                        key,
                        moment(value).format('YYYY-MM-DD')
                    );

                    continue;
                }
                
                params = params.set(key, value);
            }
        }

        return this.http.get(`${this.endpoint}${path}`, { headers, params, responseType: 'text' });
    }

    getTableDetailByPath(path: string): Observable<any> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);
        return this.http.get<any>(`${this.endpoint}${path}`, { headers });
    }

    getConfiguracionAlertasList(): Observable<IConfiguracionAlerta[]> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.get<IConfiguracionAlerta[]>(`${this.endpoint}alertas/configuraciones`, { headers })
    }

    gelAlertasList(idConfiguracionCliente: number): Observable<IAlerta[]> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        const params = this.buildHttpParams({ idConfiguracionCliente });

        return this.http.get<IAlerta[]>(`${this.endpoint}alertas/listar`, { headers, params })
    }

    editConfiguracionAlertas(alertaPayload: IAlertaPayload): Observable<IAlerta> {
        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        const idUsuario = userInfo.email;
        const idOrganizacion = this._sessionService.getOrganization();

        const headers = this.getHeaders(idOrganizacion, idUsuario, this.REQUEST_ORIGIN);

        return this.http.post<IAlerta>(`${this.endpoint}alertas/editar`, alertaPayload, { headers });
    }

}
